/**
 * Draft.js utilities for safe operations
 * Prevents nullthrows errors and handles null/undefined values gracefully
 */

/**
 * Safely checks if a value is not null or undefined
 * @param {any} value - The value to check
 * @param {string} context - Context for error messages
 * @returns {boolean} - True if value is not null/undefined
 */
export const isNotNullOrUndefined = (value, context = 'Value') => {
    if (value === null || value === undefined) {
        console.warn(`${context} is null or undefined`);
        return false;
    }
    return true;
};

/**
 * Safely gets a property from an object with null checks
 * @param {any} obj - The object to get property from
 * @param {string} property - The property name
 * @param {any} fallback - Fallback value if property doesn't exist
 * @param {string} context - Context for error messages
 * @returns {any} - Property value or fallback
 */
export const safeGetProperty = (obj, property, fallback = null, context = 'Object') => {
    try {
        if (!isNotNullOrUndefined(obj, `${context} object`)) {
            return fallback;
        }
        
        if (typeof obj !== 'object') {
            console.warn(`${context} is not an object:`, typeof obj);
            return fallback;
        }
        
        const value = obj[property];
        if (!isNotNullOrUndefined(value, `${context}.${property}`)) {
            return fallback;
        }
        
        return value;
    } catch (error) {
        console.warn(`Error getting property ${property} from ${context}:`, error);
        return fallback;
    }
};

/**
 * Safely creates a Draft.js EditorState with null checks
 * @param {any} contentState - The content state
 * @param {any} decorator - The decorator
 * @param {any} editorState - The editor state
 * @returns {any} - Safe EditorState or null
 */
export const safeCreateEditorState = (contentState, decorator = null, editorState = null) => {
    try {
        // Check if Draft.js is available
        if (typeof window !== 'undefined' && window.Draft) {
            const { EditorState, ContentState } = window.Draft;
            
            if (!isNotNullOrUndefined(contentState, 'ContentState')) {
                console.warn('ContentState is null/undefined, creating empty state');
                return EditorState.createEmpty();
            }
            
            if (editorState && isNotNullOrUndefined(editorState, 'EditorState')) {
                return editorState;
            }
            
            if (decorator && isNotNullOrUndefined(decorator, 'Decorator')) {
                return EditorState.createWithContent(contentState, decorator);
            }
            
            return EditorState.createWithContent(contentState);
        }
        
        console.warn('Draft.js is not available');
        return null;
    } catch (error) {
        console.error('Error creating EditorState:', error);
        return null;
    }
};

/**
 * Safely updates Draft.js EditorState
 * @param {any} editorState - The current editor state
 * @param {Function} updater - The update function
 * @param {any} fallback - Fallback editor state
 * @returns {any} - Updated EditorState or fallback
 */
export const safeUpdateEditorState = (editorState, updater, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(editorState, 'EditorState')) {
            console.warn('EditorState is null/undefined, cannot update');
            return fallback;
        }
        
        if (typeof updater !== 'function') {
            console.warn('Updater is not a function');
            return editorState;
        }
        
        const updatedState = updater(editorState);
        
        if (!isNotNullOrUndefined(updatedState, 'Updated EditorState')) {
            console.warn('Updated EditorState is null/undefined, returning original');
            return editorState;
        }
        
        return updatedState;
    } catch (error) {
        console.error('Error updating EditorState:', error);
        return editorState || fallback;
    }
};

/**
 * Safely gets content from Draft.js EditorState
 * @param {any} editorState - The editor state
 * @param {string} fallback - Fallback content
 * @returns {string} - Content or fallback
 */
export const safeGetContent = (editorState, fallback = '') => {
    try {
        if (!isNotNullOrUndefined(editorState, 'EditorState')) {
            return fallback;
        }
        
        const contentState = editorState.getCurrentContent();
        if (!isNotNullOrUndefined(contentState, 'ContentState')) {
            return fallback;
        }
        
        return contentState.getPlainText();
    } catch (error) {
        console.error('Error getting content from EditorState:', error);
        return fallback;
    }
};

/**
 * Safely handles Draft.js selection
 * @param {any} editorState - The editor state
 * @param {any} fallback - Fallback selection
 * @returns {any} - Selection or fallback
 */
export const safeGetSelection = (editorState, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(editorState, 'EditorState')) {
            return fallback;
        }
        
        const selection = editorState.getSelection();
        if (!isNotNullOrUndefined(selection, 'Selection')) {
            return fallback;
        }
        
        return selection;
    } catch (error) {
        console.error('Error getting selection from EditorState:', error);
        return fallback;
    }
};

/**
 * Safely handles Draft.js block operations
 * @param {any} contentState - The content state
 * @param {string} blockKey - The block key
 * @param {any} fallback - Fallback block
 * @returns {any} - Block or fallback
 */
export const safeGetBlock = (contentState, blockKey, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(contentState, 'ContentState')) {
            return fallback;
        }
        
        if (!isNotNullOrUndefined(blockKey, 'BlockKey')) {
            return fallback;
        }
        
        const block = contentState.getBlockForKey(blockKey);
        if (!isNotNullOrUndefined(block, 'Block')) {
            return fallback;
        }
        
        return block;
    } catch (error) {
        console.error('Error getting block from ContentState:', error);
        return fallback;
    }
};

/**
 * Safely handles Draft.js block character list operations
 * @param {any} block - The block object
 * @param {number} offset - The character offset
 * @param {any} fallback - Fallback character
 * @returns {any} - Character or fallback
 */
export const safeGetCharacterFromBlock = (block, offset, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(block, 'Block')) {
            console.warn('Block is null/undefined, cannot get character');
            return fallback;
        }
        
        if (typeof block.getCharacterList !== 'function') {
            console.warn('Block does not have getCharacterList method');
            return fallback;
        }
        
        const characterList = block.getCharacterList();
        if (!isNotNullOrUndefined(characterList, 'CharacterList')) {
            console.warn('CharacterList is null/undefined');
            return fallback;
        }
        
        if (typeof characterList.get !== 'function') {
            console.warn('CharacterList does not have get method');
            return fallback;
        }
        
        return characterList.get(offset);
    } catch (error) {
        console.error('Error getting character from block:', error);
        return fallback;
    }
};

/**
 * Safely handles Draft.js entity removal operations
 * @param {any} entityMap - The entity map
 * @param {any} block - The block object
 * @param {number} offset - The offset
 * @param {Function} originalRemoveForBlock - The original removeForBlock function
 * @returns {any} - Result or fallback
 */
export const safeRemoveForBlock = (entityMap, block, offset, originalRemoveForBlock) => {
    try {
        if (!isNotNullOrUndefined(block, 'Block')) {
            console.warn('Block is null/undefined in removeForBlock, skipping operation');
            return null;
        }
        
        if (!isNotNullOrUndefined(entityMap, 'EntityMap')) {
            console.warn('EntityMap is null/undefined in removeForBlock, skipping operation');
            return null;
        }
        
        if (typeof originalRemoveForBlock !== 'function') {
            console.warn('Original removeForBlock function is not available');
            return null;
        }
        
        return originalRemoveForBlock(entityMap, block, offset);
    } catch (error) {
        console.error('Error in safeRemoveForBlock:', error);
        return null;
    }
};

/**
 * Safely handles Draft.js entity operations
 * @param {any} contentState - The content state
 * @param {string} entityKey - The entity key
 * @param {any} fallback - Fallback entity
 * @returns {any} - Entity or fallback
 */
export const safeGetEntity = (contentState, entityKey, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(contentState, 'ContentState')) {
            return fallback;
        }
        
        if (!isNotNullOrUndefined(entityKey, 'EntityKey')) {
            return fallback;
        }
        
        const entity = contentState.getEntity(entityKey);
        if (!isNotNullOrUndefined(entity, 'Entity')) {
            return fallback;
        }
        
        return entity;
    } catch (error) {
        console.error('Error getting entity from ContentState:', error);
        return fallback;
    }
};

/**
 * Wraps a Draft.js operation with null checks
 * @param {Function} operation - The operation to wrap
 * @param {any} fallback - Fallback value
 * @param {string} context - Context for error messages
 * @returns {Function} - Wrapped operation
 */
export const wrapDraftOperation = (operation, fallback = null, context = 'Draft operation') => {
    return (...args) => {
        try {
            // Check all arguments for null/undefined
            for (let i = 0; i < args.length; i++) {
                if (!isNotNullOrUndefined(args[i], `${context} argument ${i}`)) {
                    console.warn(`Null/undefined argument ${i} in ${context}`);
                    return fallback;
                }
            }
            
            const result = operation(...args);
            
            if (!isNotNullOrUndefined(result, `${context} result`)) {
                console.warn(`Null/undefined result from ${context}`);
                return fallback;
            }
            
            return result;
        } catch (error) {
            console.error(`Error in ${context}:`, error);
            return fallback;
        }
    };
};

/**
 * Safely calls getIn on a potentially undefined object
 * @param {any} obj - The object to call getIn on
 * @param {Array} path - The path array for getIn
 * @param {any} fallback - Fallback value if getIn fails
 * @returns {any} - Result of getIn or fallback
 */
export const safeGetIn = (obj, path, fallback = null) => {
    try {
        if (!obj || typeof obj.getIn !== 'function') {
            console.warn('safeGetIn: obj is null/undefined or does not have getIn method');
            return fallback;
        }
        
        if (!Array.isArray(path)) {
            console.warn('safeGetIn: path is not an array');
            return fallback;
        }
        
        return obj.getIn(path);
    } catch (error) {
        console.error('Error in safeGetIn:', error);
        return fallback;
    }
};

/**
 * Safely gets block tree and calls getIn
 * @param {any} editorState - The editor state
 * @param {string} blockKey - The block key
 * @param {Array} path - The path array for getIn
 * @param {any} fallback - Fallback value if operation fails
 * @returns {any} - Result of getIn or fallback
 */
export const safeGetBlockTreeGetIn = (editorState, blockKey, path, fallback = null) => {
    try {
        if (!isNotNullOrUndefined(editorState, 'EditorState')) {
            return fallback;
        }
        
        if (!isNotNullOrUndefined(blockKey, 'BlockKey')) {
            return fallback;
        }
        
        if (!Array.isArray(path)) {
            console.warn('safeGetBlockTreeGetIn: path is not an array');
            return fallback;
        }
        
        const blockTree = editorState.getBlockTree(blockKey);
        if (!blockTree) {
            console.warn('safeGetBlockTreeGetIn: blockTree is null/undefined for key:', blockKey);
            return fallback;
        }
        
        return safeGetIn(blockTree, path, fallback);
    } catch (error) {
        console.error('Error in safeGetBlockTreeGetIn:', error);
        return fallback;
    }
};

/**
 * Sets up global nullthrows error monitoring for Draft.js
 */
export const setupDraftNullthrowsMonitoring = () => {
    // Monitor for nullthrows errors and minified Draft.js errors
    window.addEventListener('error', (event) => {
        if (!event.error || !event.error.message) {
            return;
        }
        
        const message = event.error.message;
        const stack = event.error.stack || '';
        
        // Check for Draft.js related errors (both minified and non-minified)
        const isNullthrowsError = message.includes('Got unexpected null or undefined');
        const isMinifiedDraftError = message.includes('Minified exception occurred') && 
            (stack.includes('getDraftEditorSelectionWithNodes') ||
             stack.includes('editOnSelect') ||
             stack.includes('editOnInput') ||
             stack.includes('DraftEditorCompositionHandler') ||
             stack.includes('getContentEditableContainer') ||
             stack.includes('resolveComposition') ||
             stack.includes('invariant') ||
             stack.includes('draft-js'));
        
        // Check for Draft.js TypeError patterns (getIn, getBlockTree, textContent)
        const isDraftTypeError = (message.includes('Cannot read properties of undefined') || 
                                  message.includes('undefined is not an object')) &&
            (stack.includes('editOnInput') ||
             stack.includes('editOnSelect') ||
             stack.includes('getDraftEditorSelectionWithNodes') ||
             stack.includes('getBlockTree') ||
             stack.includes('getIn') ||
             stack.includes('textContent') ||
             stack.includes('draft-js'));
        
        if (isNullthrowsError || isMinifiedDraftError || isDraftTypeError) {
            console.warn('Draft.js error detected:', {
                message: message,
                type: isDraftTypeError ? 'typeError' : (isMinifiedDraftError ? 'minified' : 'nullthrows'),
                stack: stack.substring(0, 200)
            });
            
            // Check if it's a selection/input/composition-related error
            const isSelectionError = stack && 
                (stack.includes('getDraftEditorSelectionWithNodes') ||
                 stack.includes('editOnSelect') ||
                 stack.includes('editOnInput') ||
                 stack.includes('DraftEditorCompositionHandler') ||
                 stack.includes('getContentEditableContainer') ||
                 stack.includes('resolveComposition') ||
                 stack.includes('findAncestorOffsetKey') ||
                 stack.includes('selectionchange'));
            
            if (isSelectionError || isMinifiedDraftError || isDraftTypeError) {
                console.warn('Draft.js editor error - preventing crash');
                // Prevent the error from crashing the app
                event.preventDefault();
                event.stopPropagation();
            }
            
            // Log to Sentry if available (with lower severity for known issues)
            if (window.Sentry) {
                const errorType = isDraftTypeError ? 'DRAFT_TYPEERROR' : 
                                 (isMinifiedDraftError ? 'DRAFT_MINIFIED_INVARIANT' : 'DRAFT_NULLTHROWS');
                
                window.Sentry.captureException(event.error, {
                    tags: {
                        errorType: errorType,
                        context: 'GLOBAL_ERROR_HANDLER',
                        component: 'DraftEditor',
                        selectionError: isSelectionError ? 'true' : 'false',
                        hasEditOnInput: stack.includes('editOnInput') ? 'true' : 'false'
                    },
                    level: 'warning' // These are known non-critical issues
                });
            }
        }
    }, true); // Use capture phase to catch errors early
    
    // Also monitor for selection change errors specifically
    if (typeof document !== 'undefined') {
        document.addEventListener('selectionchange', () => {
            try {
                // This is just to ensure any errors during selection change
                // are caught and don't crash the app
                const selection = window.getSelection();
                if (!selection || !selection.anchorNode) {
                    // Silently handle invalid selections
                    return;
                }
            } catch (error) {
                console.warn('Error during selectionchange event:', error);
                // Don't let selection errors crash the app
            }
        }, true);
    }
};

export default {
    isNotNullOrUndefined,
    safeGetProperty,
    safeCreateEditorState,
    safeUpdateEditorState,
    safeGetContent,
    safeGetSelection,
    safeGetBlock,
    safeGetEntity,
    wrapDraftOperation,
    setupDraftNullthrowsMonitoring
};
